import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  IconButton,
  Typography,
  Chip,
  TextField,
  Select,
  FormControl,
  MenuItem,
} from "@mhome/ui";

import { ChevronDown, ChevronUp } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

import { getTagIcon } from "../common/tag";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useIsSmallScreen } from "../common/adaptive-theme-provider";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const AutomationFilter = ({
  sortBy,
  setSortBy,
  setFilterText,
  onTagsChange,
  onEnabledChange,
  onErrorChange,
  onRunningChange,
  tags,
  type,
}) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const [searchInput, setSearchInput] = useState(urlParams.get("filter") || "");
  const [selectedTagsLocal, setSelectedTagsLocal] = useState(() => {
    const tags = urlParams.get("tags");
    return tags ? tags.split(",") : ["all"];
  });
  const [selectedEnabledLocal, setSelectedEnabledLocal] = useState(() => {
    const able = urlParams.get("able");
    return able ? able.split(",") : ["all"];
  });
  const [selectedErrorLocal, setSelectedErrorLocal] = useState(() => {
    const error = urlParams.get("err");
    return error ? error.split(",") : ["all"];
  });
  const [selectedRunningLocal, setSelectedRunningLocal] = useState(() => {
    const running = urlParams.get("running");
    return running ? running.split(",") : ["all"];
  });
  const [showAllTags, setShowAllTags] = useState(false);
  const history = useHistory();

  // Use common hook for small screen detection
  const isSmallScreen = useIsSmallScreen();
  const VISIBLE_TAGS = isSmallScreen ? 4 : 8;
  const visibleTags = showAllTags ? tags : tags.slice(0, VISIBLE_TAGS);

  const enabledOptions = ["all", "enabled", "disabled"];
  const errorOptions = ["all", "error", "no-error"];
  const runningOptions = ["all", "running", "not-running"];

  useEffect(() => {
    setFilterText(searchInput);
  }, [searchInput]);

  useEffect(() => {
    if (onTagsChange) {
      onTagsChange(selectedTagsLocal);
    }
  }, [selectedTagsLocal, onTagsChange]);

  useEffect(() => {
    if (onEnabledChange) {
      onEnabledChange(selectedEnabledLocal);
    }
  }, [selectedEnabledLocal, onEnabledChange]);

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange(selectedErrorLocal);
    }
  }, [selectedErrorLocal, onErrorChange]);

  useEffect(() => {
    if (onRunningChange) {
      onRunningChange(selectedRunningLocal);
    }
  }, [selectedRunningLocal, onRunningChange]);

  useEffect(() => {
    const updates = getUpdates();
    updateURL(updates);
  }, [
    sortBy,
    selectedTagsLocal,
    selectedEnabledLocal,
    selectedErrorLocal,
    selectedRunningLocal,
    searchInput,
  ]);

  const getUpdates = () => {
    const updates = {
      filter: searchInput,
      tags: selectedTagsLocal.join(","),
      able: selectedEnabledLocal.join(","),
      err: selectedErrorLocal.join(","),
      running: selectedRunningLocal.join(","),
      sort: sortBy,
    };

    if (Object.values(updates).some((value) => value && value !== "")) {
      if (type === "taskflow") {
        localStorage.setItem("filterParams-taskflow", JSON.stringify(updates));
      } else {
        localStorage.setItem("filterParams-recurring", JSON.stringify(updates));
      }
    }
    return updates;
  };

  const updateURL = (updates) => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (
        value &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newURL = `${location.pathname}?${currentParams.toString()}`;
    history.replace(newURL);
  };

  const handleParams = () => {
    const savedFilterParams =
      type === "taskflow"
        ? localStorage.getItem("filterParams-taskflow")
        : localStorage.getItem("filterParams-recurring");
    if (savedFilterParams) {
      const parsedParams = JSON.parse(savedFilterParams);
      return parsedParams;
    } else {
      return null;
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleClear = () => {
    setSearchInput("");
    setFilterText("");
  };

  const handleTagChange = (tag) => {
    let newTags;
    if (tag === "all") {
      newTags = ["all"];
    } else {
      const currentTags = selectedTagsLocal.filter((t) => t !== "all");
      if (currentTags.includes(tag)) {
        newTags = currentTags.filter((t) => t !== tag);
        if (newTags.length === 0) newTags = ["all"];
      } else {
        newTags = [...currentTags, tag];
      }
    }
    setSelectedTagsLocal(newTags);
  };

  const handleEnabledChange = (option) => {
    setSelectedEnabledLocal([option]);
  };

  const handleErrorChange = (option) => {
    setSelectedErrorLocal([option]);
  };

  const handleRunningChange = (option) => {
    setSelectedRunningLocal([option]);
  };

  const toggleShowAllTags = () => {
    setShowAllTags(!showAllTags);
  };

  const renderFilterOptions = (
    options,
    selectedOptions,
    handleChange,
    label
  ) => {
    return (
      <div>
        <Typography
          variant="caption"
          className="block font-medium"
          style={{
            fontSize: "0.625rem",
            color: "hsl(var(--muted-foreground))",
          }}
        >
          {label}
        </Typography>
        <div className="flex flex-wrap gap-0.5">
          {options.map((option) => (
            <Chip
              key={option}
              label={option.charAt(0).toUpperCase() + option.slice(1)}
              onClick={() => handleChange(option)}
              className="cursor-pointer h-6 rounded-xl text-xs font-medium transition-colors"
              style={{
                backgroundColor: selectedOptions.includes(option)
                  ? "hsl(var(--primary))"
                  : getComponentBgColor("hsl(var(--input))"),
                color: selectedOptions.includes(option)
                  ? "hsl(var(--primary-foreground))"
                  : "hsl(var(--foreground))",
                fontSize: "0.75rem",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  selectedOptions.includes(option)
                    ? "hsl(var(--primary-foreground))"
                    : "hsl(var(--accent))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  selectedOptions.includes(option)
                    ? "hsl(var(--primary))"
                    : getComponentBgColor("hsl(var(--input))");
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card
      className="mb-1 w-full rounded-2xl"
      style={{
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
      }}
      elevation={0}
    >
      <div className="flex flex-col p-2 px-3 w-full gap-0.5">
        <ResponsiveCardGrid
          spacing={{ xs: 0.5, sm: 1.5, md: 1.5 }}
          breakpoints={{ xs: 1, sm: 2, md: 2 }}
        >
          <div>
            <Typography
              variant="caption"
              className="block font-medium"
              style={{
                fontSize: "0.625rem",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              Name
            </Typography>
            <TextField
              fullWidth
              value={searchInput}
              onChange={handleSearchInputChange}
              onBlur={handleSearchInputChange}
              placeholder="Filter by name"
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: searchInput && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    variant="ghost"
                    className="-mr-1"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </div>

          <div>
            <Typography
              variant="caption"
              className="block font-medium"
              style={{
                fontSize: "0.625rem",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              Sort By
            </Typography>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                }}
                size="small"
              >
                <MenuItem value="executed">Executed (Latest)</MenuItem>
                <MenuItem value="opp-executed">Executed (Oldest)</MenuItem>
                <MenuItem value="created">Created (Newest)</MenuItem>
                <MenuItem value="opp-created">Created (Oldest)</MenuItem>
                <MenuItem value="name">Name (A-Z)</MenuItem>
                <MenuItem value="opp-name">Name (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </div>
        </ResponsiveCardGrid>

        {isSmallScreen ? (
          <div className="flex flex-col gap-1">
            <>
              {renderFilterOptions(
                errorOptions,
                selectedErrorLocal,
                handleErrorChange,
                "Error"
              )}
              {renderFilterOptions(
                runningOptions,
                selectedRunningLocal,
                handleRunningChange,
                "Running"
              )}
            </>
            {renderFilterOptions(
              enabledOptions,
              selectedEnabledLocal,
              handleEnabledChange,
              "Enabled"
            )}
            <div>
              <Typography
                variant="caption"
                className="block font-medium"
                style={{
                  fontSize: "0.625rem",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                Tag
              </Typography>
              <div className="flex flex-wrap gap-0.5">
                {visibleTags.map((tag) => (
                  <Chip
                    key={tag}
                    icon={tag !== "all" ? getTagIcon(tag, 16) : undefined}
                    label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                    onClick={() => handleTagChange(tag)}
                    className="cursor-pointer h-6 rounded-xl text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: selectedTagsLocal.includes(tag)
                        ? "hsl(var(--primary))"
                        : getComponentBgColor("hsl(var(--input))"),
                      color: selectedTagsLocal.includes(tag)
                        ? "hsl(var(--primary-foreground))"
                        : "hsl(var(--foreground))",
                      fontSize: "0.75rem",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        selectedTagsLocal.includes(tag)
                          ? "hsl(var(--primary-foreground))"
                          : "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        selectedTagsLocal.includes(tag)
                          ? "hsl(var(--primary))"
                          : getComponentBgColor("hsl(var(--input))");
                    }}
                  />
                ))}
                {tags.length > VISIBLE_TAGS && (
                  <Button
                    onClick={toggleShowAllTags}
                    variant="ghost"
                    startIcon={
                      showAllTags ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )
                    }
                    className="min-w-0 px-2 py-1 h-6 text-xs rounded-md"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                      fontSize: "0.75rem",
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {showAllTags ? "Less" : "More"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {renderFilterOptions(
              errorOptions,
              selectedErrorLocal,
              handleErrorChange,
              "Error"
            )}
            {renderFilterOptions(
              enabledOptions,
              selectedEnabledLocal,
              handleEnabledChange,
              "Enabled"
            )}
            {renderFilterOptions(
              runningOptions,
              selectedRunningLocal,
              handleRunningChange,
              "Running"
            )}
            <div>
              <Typography
                variant="caption"
                className="block font-medium"
                style={{
                  fontSize: "0.625rem",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                Tag
              </Typography>
              <div className="flex flex-wrap gap-0.5">
                {visibleTags.map((tag) => (
                  <Chip
                    key={tag}
                    icon={tag !== "all" ? getTagIcon(tag, 16) : undefined}
                    label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                    onClick={() => handleTagChange(tag)}
                    className="cursor-pointer h-6 rounded-xl text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: selectedTagsLocal.includes(tag)
                        ? "hsl(var(--primary))"
                        : getComponentBgColor("hsl(var(--input))"),
                      color: selectedTagsLocal.includes(tag)
                        ? "hsl(var(--primary-foreground))"
                        : "hsl(var(--foreground))",
                      fontSize: "0.75rem",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        selectedTagsLocal.includes(tag)
                          ? "hsl(var(--primary-foreground))"
                          : "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        selectedTagsLocal.includes(tag)
                          ? "hsl(var(--primary))"
                          : getComponentBgColor("hsl(var(--input))");
                    }}
                  />
                ))}
                {tags.length > VISIBLE_TAGS && (
                  <Button
                    onClick={toggleShowAllTags}
                    variant="ghost"
                    startIcon={
                      showAllTags ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )
                    }
                    className="min-w-0 px-2 py-1 h-6 text-xs rounded-md"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                      fontSize: "0.75rem",
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {showAllTags ? "Less" : "More"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AutomationFilter;
